"use client";

import { useEffect, useState } from "react";
import { Menu, ShoppingCart } from "lucide-react";
import { useTranslations } from "next-intl";

import { Link, usePathname } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { LocalePicker } from "@/components/layout/LocalePicker";
import { Wordmark } from "@/components/layout/Wordmark";
import { ORDERING_PAUSED } from "@/lib/ordering";
import { cn } from "@/lib/utils/cn";
import { Drawer } from "@/components/ui/drawer";
import { useCartCount } from "@/features/cart/CartCountProvider";
import { useCartNavigation } from "@/features/cart/use-cart-navigation";

type NavItem = {
  key: "design" | "themes" | "articles" | "how";
  href: string;
  match?: string;
};

const NAV_ITEMS: readonly NavItem[] = [
  { key: "design", href: "/configure", match: "/configure" },
  { key: "themes", href: "/#themes", match: "/#themes" },
  { key: "articles", href: "/articles", match: "/articles" },
  { key: "how", href: "/#how", match: "/#how" },
] as const;

function isPathActive(pathname: string, match: string | undefined) {
  if (!match || match.startsWith("/#")) return false;
  if (match === "/") return pathname === "/";
  return pathname === match || pathname.startsWith(`${match}/`);
}

/**
 * The badge slot on the cart icon: a spinner while an add-to-cart is in
 * flight, the item count once it has settled, nothing when the cart is empty.
 */
function CartBadge({
  busy,
  count,
  className,
}: {
  busy: boolean;
  count: number;
  className?: string;
}) {
  if (busy) {
    return (
      <span
        aria-hidden
        className={cn("inline-flex size-5 items-center justify-center", className)}
      >
        <span
          className={cn(
            "size-4 animate-spin rounded-full border-2 border-cobalt-dark/20 border-t-cobalt-dark",
            // A frozen arc would read as broken rather than busy, so reduced
            // motion gets a pulse instead of a stopped spinner.
            "motion-reduce:animate-pulse motion-reduce:border-cobalt-dark",
          )}
        />
      </span>
    );
  }

  if (count <= 0) return null;

  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-5 items-center justify-center rounded-full bg-cobalt text-xs font-medium leading-none text-paper tabular-nums",
        className,
      )}
    >
      {count}
    </span>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const t = useTranslations("nav");
  const th = useTranslations("header");
  const { displayCount: cartCount, isAddInFlight } = useCartCount();
  const {
    href: cartHref,
    onClick: onCartClick,
    isParked: cartNavigationParked,
    isNavigating: cartNavigating,
  } = useCartNavigation();

  // On the landing page the header starts transparent over the hero's cobalt
  // wash and turns solid once the page scrolls.
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);
  const transparent = isHome && !scrolled;

  const cartActive = isPathActive(pathname, "/cart");
  const cartCountLabel = cartCount > 0 ? th("cartCountLabel", { count: cartCount }) : null;
  // While the add is on the wire the badge is a spinner rather than a number:
  // the new total isn't known yet, and a number that lands early reads as
  // "done". `aria-busy` carries the same news to assistive tech.
  const cartAriaLabel = isAddInFlight
    ? th("cartBusyLabel")
    : (cartCountLabel ?? th("cartLabel"));
  // Parked and navigating both pulse: from the buyer's side each one is "my
  // click landed, the cart is on its way".
  const cartBusy = isAddInFlight || cartNavigationParked || cartNavigating;

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full border-b transition-colors duration-200",
        transparent ? "border-transparent bg-transparent" : "border-paper-3 bg-paper"
      )}
    >
      <div className="relative mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-6">
        {/* Left: mobile hamburger + desktop brand */}
        <div className="flex items-center">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setMobileOpen(true)}
            aria-label={th("openMenu")}
            className="md:hidden"
          >
            <Menu className="size-5" />
          </Button>

          <Link href="/" aria-label="Märka home" className="hidden md:block">
            <Wordmark className="text-2xl text-ink" />
          </Link>
        </div>

        {/* Mobile centered brand */}
        <Link
          href="/"
          aria-label="Märka home"
          className="absolute left-1/2 -translate-x-1/2 md:hidden"
        >
          <Wordmark className="text-2xl text-ink" />
        </Link>

        {/* Center: desktop nav, absolutely centered */}
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
          {NAV_ITEMS.map((item) => {
            const active = isPathActive(pathname, item.match);
            return (
              <Link
                key={item.key}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                href={item.href as any}
                className={cn(
                  "relative font-sans text-sm transition-colors hover:text-ink",
                  active ? "text-ink" : "text-ink-2"
                )}
              >
                {t(item.key)}
                {active && (
                  <span
                    aria-hidden
                    className="absolute -bottom-2 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-cobalt"
                  />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Right: cart */}
        <div className="flex items-center">
          {/* Shop paused (src/lib/ordering.ts): the cart page is closed. */}
          {ORDERING_PAUSED ? null : (
            <>
              {/* Desktop cart pill — plain <a> (not next-intl <Link>) so navigation
                  triggers a full reload, bypassing React Activity's preservation of
                  the previous cart RSC payload. `onCartClick` parks that load while
                  an add-to-cart is in flight, so it can't abort the POST. */}
              <a
                href={cartHref}
                onClick={onCartClick}
                aria-busy={cartBusy}
                aria-label={cartAriaLabel}
                className={cn(
                  "hidden h-11 items-center gap-2 rounded-full bg-paper-2 px-4 font-sans text-sm text-ink transition-colors hover:bg-paper-3 md:inline-flex",
                  cartActive && "bg-paper-3",
                  (cartNavigationParked || cartNavigating) && "animate-pulse"
                )}
              >
                <ShoppingCart className="size-4" />
                <span>{th("cartLabel")}</span>
                <CartBadge busy={isAddInFlight} count={cartCount} className="ml-1" />
              </a>

              {/* Mobile cart — see desktop comment above. */}
              <a
                href={cartHref}
                onClick={onCartClick}
                aria-busy={cartBusy}
                aria-label={cartAriaLabel}
                className={cn(
                  "relative inline-flex size-11 items-center justify-center rounded-full bg-paper-2 text-ink transition-colors hover:bg-paper-3 md:hidden",
                  cartActive && "bg-paper-3",
                  (cartNavigationParked || cartNavigating) && "animate-pulse"
                )}
              >
                <ShoppingCart className="size-5" />
                <CartBadge
                  busy={isAddInFlight}
                  count={cartCount}
                  className="absolute -top-1 -right-1"
                />
              </a>
            </>
          )}
        </div>
      </div>

      {/* Mobile sheet menu */}
      <Drawer
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        side="right"
        title={
          <Link
            href="/"
            onClick={() => setMobileOpen(false)}
            className="rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            Märka
          </Link>
        }
      >
        <nav className="flex flex-col gap-1 pb-4">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              href={item.href as any}
              onClick={() => setMobileOpen(false)}
              className="rounded-xl px-3 py-3 font-heading text-2xl text-ink hover:bg-paper-2"
            >
              {t(item.key)}
            </Link>
          ))}
        </nav>
        <div className="mt-2 flex flex-col gap-3 border-t border-paper-3 pt-4">
          {ORDERING_PAUSED ? null : (
            <Button
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render={<Link href={"/configure" as any} />}
              nativeButton={false}
              onClick={() => setMobileOpen(false)}
              className="w-full"
            >
              {th("designCta")}
            </Button>
          )}
          <LocalePicker />
        </div>
      </Drawer>
    </header>
  );
}
